// Kleines Werkzeug für Oberflächen in einfachem JavaScript (ohne React): Elemente bauen (`h`), Ansicht nach einer Zustandsänderung in das vorhandene DOM einarbeiten (`morph`,
// damit Eingabefokus, Cursor und aufgeklappte Bereiche erhalten bleiben) und Ereignisse über Attribute zuordnen (`mount`). Gedacht für die Vorlage der WohnBau-Apps.
//
// Elemente mit data-slot sind Platzhalter für fremden Inhalt (z. B. einen React-Teil in einer Brücke): `morph` lässt ihren Inhalt unberührt.
//
// Ansichten sind reine Funktionen `zustand -> Element`. Ereignisse stehen als Attribute im Markup: data-click="name", data-input="name", data-change="name", data-enter="name", dazu data-toggle (Aufklappen eines <details>), data-activate (Enter oder Leertaste auf einem fokussierten Element, z. B. einer Karte) und data-error (Bild lässt sich nicht laden).
// `mount` ruft dann `aktionen[name](element, ereignis)` auf. So bleibt das Markup frei von Funktionen und lässt sich mit der React-Fassung vergleichen.

/** Element mit Attributen und Kindern (Text wird nie als HTML gelesen). `false`, `undefined` und `null` entfallen, `true` setzt ein leeres Attribut. */
export function h(tag, attributes, ...children) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes || {})) {
    if (value === false || value === undefined || value === null) continue;
    element.setAttribute(name, value === true ? '' : String(value));
  }
  for (const child of children.flat(Infinity)) {
    if (child === false || child === undefined || child === null || child === '') continue;
    element.append(child);
  }
  if (tag === 'input' && element.hasAttribute('value')) element.value = element.getAttribute('value');
  if (tag === 'select') {
    const chosen = element.querySelector('option[selected]');
    if (chosen) element.value = chosen.value;
  }
  return element;
}

/** Wie `h`, aber für SVG-Elemente (eigener Namensraum). */
export function svg(tag, attributes, ...children) {
  const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [name, value] of Object.entries(attributes || {})) {
    if (value === false || value === undefined || value === null) continue;
    element.setAttribute(name, value === true ? '' : String(value));
  }
  for (const child of children.flat(Infinity)) {
    if (child === false || child === undefined || child === null || child === '') continue;
    element.append(child);
  }
  return element;
}

function sameKind(a, b) {
  return a.nodeType === b.nodeType && (a.nodeType !== 1 || (a.tagName === b.tagName && a.id === b.id));
}

function syncAttributes(from, to) {
  for (const { name } of Array.from(from.attributes)) {
    if (!to.hasAttribute(name) && name !== 'open') from.removeAttribute(name);
  }
  for (const { name, value } of Array.from(to.attributes)) {
    if (name === 'open') { if (!from.hasAttribute('open')) from.setAttribute('open', ''); continue; }   // aufklappen ja, selbst zuklappen nie (der Benutzer klappt auf und zu)
    if (from.getAttribute(name) !== value) from.setAttribute(name, value);
  }
}

function syncValue(from, to) {
  if (from.tagName === 'INPUT') {
    const type = (to.getAttribute('type') || 'text').toLowerCase();
    if (type === 'file') return;
    if (type === 'checkbox' || type === 'radio') { if (from.checked !== to.hasAttribute('checked')) from.checked = to.hasAttribute('checked'); return; }
    const value = to.getAttribute('value') ?? '';
    if (from.value !== value) from.value = value;
  } else if (from.tagName === 'TEXTAREA') {
    if (from.value !== to.textContent) from.value = to.textContent;
  }
}

/** Arbeitet `to` (neue Ansicht) so in `from` (vorhandenes Element) ein, dass unveränderte Elemente bestehen bleiben. */
export function morph(from, to) {
  syncAttributes(from, to);
  syncValue(from, to);
  if (to.hasAttribute('data-slot')) return;   // Platzhalter für fremden Inhalt (z. B. React-Teil): nicht anfassen
  const old = Array.from(from.childNodes);
  const next = Array.from(to.childNodes);
  for (let i = 0; i < next.length; i += 1) {
    if (i >= old.length) { from.append(next[i]); continue; }
    if (!sameKind(old[i], next[i])) { old[i].replaceWith(next[i]); continue; }
    if (old[i].nodeType === 1) morph(old[i], next[i]);
    else if (old[i].nodeValue !== next[i].nodeValue) old[i].nodeValue = next[i].nodeValue;
  }
  for (let i = next.length; i < old.length; i += 1) old[i].remove();
  if (from.tagName === 'SELECT' && from.value !== to.value) from.value = to.value;
}

/**
 * Zeigt `view(zustand)` in `root` und ordnet Ereignisse den `aktionen` zu. `update(zustand, aktionen)` zeichnet neu (nur geänderte Stellen);
 * `aktionen` darf bei jedem Aufruf neu sein (frische Funktionen mit dem aktuellen Zustand).
 */
export function mount(root, view, actions) {
  let latest = actions;
  let current;
  const listeners = [];
  const listen = (type, attribute, accept, capture) => {
    const handler = event => {
      if (accept && !accept(event)) return;
      const element = event.target instanceof Element ? event.target.closest(`[${attribute}]`) : null;
      if (!element || !root.contains(element)) return;
      const action = latest[element.getAttribute(attribute)];
      if (action) action(element, event);
    };
    root.addEventListener(type, handler, capture);
    listeners.push([type, handler, capture]);
  };
  listen('click', 'data-click');
  listen('input', 'data-input');
  listen('change', 'data-change');
  listen('keydown', 'data-enter', event => event.key === 'Enter');
  // data-activate: Element lässt sich mit Enter oder Leertaste „anklicken“ (z. B. eine Karte mit tabindex), nur wenn es selbst den Fokus hat
  const activate = event => {
    if ((event.key !== 'Enter' && event.key !== ' ') || !(event.target instanceof Element) || !event.target.hasAttribute('data-activate')) return;
    const action = latest[event.target.getAttribute('data-activate')];
    if (action && root.contains(event.target)) { event.preventDefault(); action(event.target, event); }
  };
  root.addEventListener('keydown', activate);
  listeners.push(['keydown', activate, undefined]);
  listen('error', 'data-error', undefined, true);   // „error“ (z. B. ein Bild lässt sich nicht laden) steigt nicht auf
  listen('toggle', 'data-toggle', undefined, true);   // „toggle“ (aufklappen) steigt nicht auf, wird deshalb schon beim Hinabreichen abgefangen
  return {
    update(state, nextActions) {
      if (nextActions) latest = nextActions;
      const next = view(state);
      if (!current || !current.isConnected) { root.replaceChildren(next); current = next; } else morph(current, next);
    },
    destroy() { for (const [type, handler, capture] of listeners) root.removeEventListener(type, handler, capture); root.replaceChildren(); current = undefined; }
  };
}
